import React, { useState, useRef } from 'react';
import {
  FaPlane, FaPlaneDeparture, FaPlaneArrival, FaPlus, FaTimes,
  FaCheck, FaLock, FaCalendarAlt, FaClock, FaCamera, FaSearch,
  FaEdit, FaUpload, FaSpinner, FaCheckCircle, FaExclamationTriangle
} from 'react-icons/fa';
import { scanTicketPreview, lookupFlight, searchAirports } from '../../api/client';

const DEMO_FLIGHT_LIMIT = 1;

const DemoFlightManager = ({ startDate, endDate, onGetStarted }) => {
  const [flights, setFlights] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMethod, setAddMethod] = useState(null); // 'scan', 'lookup', 'manual'
  const [step, setStep] = useState('choose'); // 'choose', 'input', 'confirm'
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [scannedData, setScannedData] = useState(null);
  const [authRequired, setAuthRequired] = useState(false);

  // Manual form state
  const [flightType, setFlightType] = useState('outbound');
  const [formData, setFormData] = useState({
    flight_number: '',
    airline: '',
    departure_airport_iata: '',
    departure_date: startDate || '',
    departure_time: '',
    arrival_airport_iata: '',
    arrival_date: startDate || '',
    arrival_time: '',
  });
  const [airportSuggestions, setAirportSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(null); // 'departure' or 'arrival'
  const [lookingUpFlight, setLookingUpFlight] = useState(false);
  const searchTimeout = useRef(null);
  const lookupTimeout = useRef(null);

  const isLimitReached = flights.length >= DEMO_FLIGHT_LIMIT;

  const resetModal = () => {
    setShowAddModal(false);
    setAddMethod(null);
    setStep('choose');
    setError(null);
    setScannedData(null);
    setIsProcessing(false);
    setAuthRequired(false);
    setFormData({
      flight_number: '', airline: '', departure_airport_iata: '',
      departure_date: startDate || '', departure_time: '',
      arrival_airport_iata: '', arrival_date: startDate || '', arrival_time: '',
    });
  };

  const handleMethodSelect = (m) => {
    setAddMethod(m);
    setStep('input');
    setError(null);
    setAuthRequired(false);
  };

  // Scan ticket via API
  const handleScanTicket = async (file) => {
    setIsProcessing(true);
    setError(null);
    setAuthRequired(false);
    try {
      const result = await scanTicketPreview(file);
      if (result.success && result.flights && result.flights.length > 0) {
        setScannedData(result.flights);
        setStep('confirm');
      } else {
        setError(result.error || 'Could not extract flight info. Try manual entry.');
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAuthRequired(true);
        setError('Sign up for a free account to use ticket scanning. You can still enter details manually.');
      } else {
        setError(err.response?.data?.detail || 'Failed to scan ticket. Try manual entry.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Lookup flight via API
  const handleLookupFlight = async (flightNumber, date) => {
    setIsProcessing(true);
    setError(null);
    setAuthRequired(false);
    try {
      const result = await lookupFlight(flightNumber, date);
      if (result.found) {
        setScannedData([{
          flight_type: 'outbound',
          departure_airport_iata: result.departure_airport_iata,
          departure_date: result.departure_date,
          departure_time: result.departure_time,
          arrival_airport_iata: result.arrival_airport_iata,
          arrival_date: result.arrival_date,
          arrival_time: result.arrival_time,
          airline: result.airline,
          flight_number: result.flight_number,
        }]);
        setStep('confirm');
      } else if (result.airline) {
        // Partial match - prefill manual form
        setFormData(prev => ({
          ...prev,
          airline: result.airline,
          flight_number: result.flight_number || flightNumber,
          departure_date: date,
        }));
        setError(`Detected ${result.airline}. Switching to manual entry for full details.`);
        setTimeout(() => { setAddMethod('manual'); setError(null); }, 2000);
      } else {
        setError(result.error || 'Flight not found. Check the number and date, or use manual entry.');
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAuthRequired(true);
        setError('Sign up for a free account to use flight lookup. You can still enter details manually.');
      } else {
        setError(err.response?.data?.detail || 'Lookup failed. Try manual entry.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Airport search for manual entry
  const handleAirportSearch = async (query, field) => {
    if (query.length < 2) {
      setAirportSuggestions([]);
      return;
    }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const result = await searchAirports(query);
        setAirportSuggestions(result.airports || []);
        setShowSuggestions(field);
      } catch {
        // Silently fail - user can type IATA code directly
      }
    }, 300);
  };

  const selectAirport = (airport, field) => {
    if (field === 'departure') {
      setFormData(prev => ({ ...prev, departure_airport_iata: airport.iata }));
    } else {
      setFormData(prev => ({ ...prev, arrival_airport_iata: airport.iata }));
    }
    setShowSuggestions(null);
    setAirportSuggestions([]);
  };

  // Auto-lookup flight number
  const handleFlightNumberChange = (value) => {
    const upper = value.toUpperCase();
    setFormData(prev => ({ ...prev, flight_number: upper }));

    clearTimeout(lookupTimeout.current);
    const pattern = /^[A-Z]{2}\d{1,4}$/;
    if (!pattern.test(upper) || !formData.departure_date) return;

    lookupTimeout.current = setTimeout(async () => {
      setLookingUpFlight(true);
      try {
        const result = await lookupFlight(upper, formData.departure_date);
        if (result.found) {
          setFormData(prev => ({
            ...prev,
            departure_airport_iata: result.departure_airport_iata || prev.departure_airport_iata,
            arrival_airport_iata: result.arrival_airport_iata || prev.arrival_airport_iata,
            departure_time: result.departure_time || prev.departure_time,
            arrival_time: result.arrival_time || prev.arrival_time,
            arrival_date: result.arrival_date || prev.arrival_date,
            airline: result.airline || prev.airline,
          }));
        } else if (result.airline) {
          setFormData(prev => ({ ...prev, airline: result.airline || prev.airline }));
        }
      } catch { /* silent */ }
      finally { setLookingUpFlight(false); }
    }, 500);
  };

  // Confirm scanned flights
  const handleConfirmScanned = () => {
    if (!scannedData || scannedData.length === 0) return;
    const f = scannedData[0];
    const newFlight = {
      id: `demo-flight-${Date.now()}`,
      type: f.flight_type || 'outbound',
      flight_number: f.flight_number || '',
      airline: f.airline || '',
      departure_airport: f.departure_airport_iata || '',
      arrival_airport: f.arrival_airport_iata || '',
      departure_date: f.departure_date || '',
      departure_time: f.departure_time || '',
      arrival_date: f.arrival_date || '',
      arrival_time: f.arrival_time || '',
    };
    setFlights(prev => [...prev, newFlight]);
    resetModal();
  };

  // Manual submit
  const handleManualSubmit = () => {
    if (!formData.departure_airport_iata || !formData.arrival_airport_iata) return;
    const newFlight = {
      id: `demo-flight-${Date.now()}`,
      type: flightType,
      flight_number: formData.flight_number,
      airline: formData.airline,
      departure_airport: formData.departure_airport_iata,
      arrival_airport: formData.arrival_airport_iata,
      departure_date: formData.departure_date,
      departure_time: formData.departure_time,
      arrival_date: formData.arrival_date,
      arrival_time: formData.arrival_time,
    };
    setFlights(prev => [...prev, newFlight]);
    resetModal();
  };

  const handleDeleteFlight = (id) => {
    setFlights(prev => prev.filter(f => f.id !== id));
  };

  return (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-neutral-800 flex items-center gap-3 pb-3 border-b border-neutral-300">
        <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-600 rounded-full flex items-center justify-center">
          <FaPlane className="text-white text-xl" />
        </div>
        Flights
      </h3>

      {/* Demo Banner */}
      <div className="flex items-center justify-between p-3 rounded-lg border bg-sky-50 border-sky-200">
        <p className="text-sm font-medium text-sky-700">
          Demo mode: {flights.length}/{DEMO_FLIGHT_LIMIT} flight (local only). Subscribe to Solo plan for unlimited flight management.
        </p>
      </div>

      {/* Flight Cards */}
      {flights.map((flight) => (
        <div key={flight.id} className="bg-white rounded-xl shadow-md border border-neutral-200 p-5 relative">
          <button onClick={() => handleDeleteFlight(flight.id)} className="absolute top-3 right-3 p-2 text-neutral-400 hover:text-red-500 transition-colors">
            <FaTimes />
          </button>
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${flight.type === 'outbound' ? 'bg-sky-100 text-sky-700' : 'bg-indigo-100 text-indigo-700'}`}>
              {flight.type === 'outbound' ? 'Outbound' : 'Return'}
            </span>
            <span className="font-bold text-neutral-800">{flight.airline} {flight.flight_number}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <FaPlaneDeparture className="text-sky-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-neutral-800">{flight.departure_airport}</p>
              {flight.departure_time && <p className="text-sm text-neutral-500 flex items-center gap-1 justify-center"><FaClock className="text-xs" /> {flight.departure_time}</p>}
              {flight.departure_date && <p className="text-xs text-neutral-400">{flight.departure_date}</p>}
            </div>
            <div className="flex-1 flex items-center">
              <div className="flex-1 h-px bg-neutral-300 relative">
                <FaPlane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sky-400 bg-white px-1" />
              </div>
            </div>
            <div className="text-center">
              <FaPlaneArrival className="text-sky-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-neutral-800">{flight.arrival_airport}</p>
              {flight.arrival_time && <p className="text-sm text-neutral-500 flex items-center gap-1 justify-center"><FaClock className="text-xs" /> {flight.arrival_time}</p>}
              {flight.arrival_date && <p className="text-xs text-neutral-400">{flight.arrival_date}</p>}
            </div>
          </div>
        </div>
      ))}

      {/* Add Flight Button */}
      {!showAddModal && (
        <button
          onClick={() => !isLimitReached && setShowAddModal(true)}
          disabled={isLimitReached}
          className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
            isLimitReached ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : 'bg-gradient-to-r from-sky-400 to-sky-500 text-white hover:shadow-lg hover:scale-[1.02]'
          }`}
        >
          {isLimitReached ? <><FaLock /> Demo limit reached - Subscribe to Solo plan</> : <><FaPlus /> Add Flight</>}
        </button>
      )}

      {/* Empty State */}
      {flights.length === 0 && !showAddModal && (
        <div className="text-center py-8 bg-neutral-50 rounded-xl border-2 border-dashed border-neutral-300">
          <div className="w-16 h-16 bg-neutral-200 rounded-full flex items-center justify-center mx-auto mb-3">
            <FaPlane className="text-3xl text-neutral-400" />
          </div>
          <p className="text-neutral-600 font-medium">No flights added yet. Add your flight details!</p>
        </div>
      )}

      {/* Add Flight Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-neutral-800 flex items-center gap-2">
                <FaPlane className="text-sky-500" /> Add Flight
              </h3>
              <button onClick={resetModal} className="p-2 text-neutral-400 hover:text-neutral-600"><FaTimes /></button>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <FaExclamationTriangle className="text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-red-700">{error}</span>
                    {authRequired && onGetStarted && (
                      <button onClick={onGetStarted} className="block mt-2 px-4 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600">
                        Sign Up Free
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Method Selection */}
              {step === 'choose' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button onClick={() => handleMethodSelect('scan')} className="p-6 border-2 border-neutral-200 rounded-xl hover:border-sky-400 hover:bg-sky-50 transition-all text-center group">
                    <FaCamera className="text-4xl text-sky-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <h4 className="font-semibold text-neutral-800 mb-2">Scan Ticket</h4>
                    <p className="text-sm text-neutral-500">Upload a photo or PDF of your ticket</p>
                  </button>
                  <button onClick={() => handleMethodSelect('lookup')} className="p-6 border-2 border-neutral-200 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all text-center group">
                    <FaSearch className="text-4xl text-green-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <h4 className="font-semibold text-neutral-800 mb-2">Flight Number</h4>
                    <p className="text-sm text-neutral-500">Enter your flight number to auto-fill</p>
                  </button>
                  <button onClick={() => handleMethodSelect('manual')} className="p-6 border-2 border-neutral-200 rounded-xl hover:border-amber-400 hover:bg-amber-50 transition-all text-center group">
                    <FaEdit className="text-4xl text-amber-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <h4 className="font-semibold text-neutral-800 mb-2">Manual Entry</h4>
                    <p className="text-sm text-neutral-500">Enter flight details manually</p>
                  </button>
                </div>
              )}

              {/* Scan Input */}
              {step === 'input' && addMethod === 'scan' && (
                <ScanInput onScan={handleScanTicket} isProcessing={isProcessing} onBack={() => { setStep('choose'); setAddMethod(null); setError(null); }} />
              )}

              {/* Lookup Input */}
              {step === 'input' && addMethod === 'lookup' && (
                <LookupInput onLookup={handleLookupFlight} isProcessing={isProcessing} onBack={() => { setStep('choose'); setAddMethod(null); setError(null); }} startDate={startDate} endDate={endDate} />
              )}

              {/* Manual Input */}
              {step === 'input' && addMethod === 'manual' && (
                <div className="space-y-4">
                  <div className="flex gap-4 mb-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" value="outbound" checked={flightType === 'outbound'} onChange={(e) => setFlightType(e.target.value)} className="text-sky-500" />
                      <span>Outbound</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" value="return" checked={flightType === 'return'} onChange={(e) => setFlightType(e.target.value)} className="text-sky-500" />
                      <span>Return</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Flight Number</label>
                      <div className="relative">
                        <input type="text" value={formData.flight_number} onChange={(e) => handleFlightNumberChange(e.target.value)} placeholder="e.g., UA123" className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-sky-500" />
                        {lookingUpFlight && <FaSpinner className="absolute right-3 top-3 animate-spin text-sky-500" />}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Airline</label>
                      <input type="text" value={formData.airline} onChange={(e) => setFormData(prev => ({ ...prev, airline: e.target.value }))} placeholder="e.g., United Airlines" className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-sky-500" />
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Departure Airport *</label>
                      <input type="text" value={formData.departure_airport_iata} onChange={(e) => { setFormData(prev => ({ ...prev, departure_airport_iata: e.target.value.toUpperCase() })); handleAirportSearch(e.target.value, 'departure'); }} placeholder="e.g., LAX" className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-sky-500" />
                      {showSuggestions === 'departure' && airportSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {airportSuggestions.map((a, i) => (
                            <button key={i} onClick={() => selectAirport(a, 'departure')} className="w-full px-3 py-2 text-left text-sm hover:bg-sky-50 flex items-center gap-2">
                              <span className="font-bold text-sky-600">{a.iata}</span> <span className="text-neutral-600">{a.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Arrival Airport *</label>
                      <input type="text" value={formData.arrival_airport_iata} onChange={(e) => { setFormData(prev => ({ ...prev, arrival_airport_iata: e.target.value.toUpperCase() })); handleAirportSearch(e.target.value, 'arrival'); }} placeholder="e.g., NRT" className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-sky-500" />
                      {showSuggestions === 'arrival' && airportSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {airportSuggestions.map((a, i) => (
                            <button key={i} onClick={() => selectAirport(a, 'arrival')} className="w-full px-3 py-2 text-left text-sm hover:bg-sky-50 flex items-center gap-2">
                              <span className="font-bold text-sky-600">{a.iata}</span> <span className="text-neutral-600">{a.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Departure Date</label>
                      <input type="date" value={formData.departure_date} onChange={(e) => setFormData(prev => ({ ...prev, departure_date: e.target.value }))} className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Departure Time</label>
                      <input type="time" value={formData.departure_time} onChange={(e) => setFormData(prev => ({ ...prev, departure_time: e.target.value }))} className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Arrival Date</label>
                      <input type="date" value={formData.arrival_date} onChange={(e) => setFormData(prev => ({ ...prev, arrival_date: e.target.value }))} className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-sky-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Arrival Time</label>
                      <input type="time" value={formData.arrival_time} onChange={(e) => setFormData(prev => ({ ...prev, arrival_time: e.target.value }))} className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-sky-500" />
                    </div>
                  </div>

                  <div className="flex gap-3 justify-between pt-2">
                    <button type="button" onClick={() => { setStep('choose'); setAddMethod(null); setError(null); }} className="text-neutral-500 hover:text-neutral-700">Back to options</button>
                    <button onClick={handleManualSubmit} disabled={!formData.departure_airport_iata || !formData.arrival_airport_iata} className="px-6 py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 disabled:opacity-50 flex items-center gap-2">
                      <FaCheck /> Add Flight
                    </button>
                  </div>
                </div>
              )}

              {/* Confirm Scanned Data */}
              {step === 'confirm' && scannedData && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FaCheckCircle className="text-green-500 text-xl" />
                      <h4 className="font-semibold text-green-800">Flight Details Found</h4>
                    </div>
                    {scannedData.map((f, i) => (
                      <div key={i} className="bg-white rounded-lg p-4 border border-green-200">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <FaPlaneDeparture className="text-sky-500 mx-auto mb-1" />
                            <p className="text-lg font-bold">{f.departure_airport_iata || '???'}</p>
                            {f.departure_time && <p className="text-sm text-neutral-500">{f.departure_time}</p>}
                            {f.departure_date && <p className="text-xs text-neutral-400">{f.departure_date}</p>}
                          </div>
                          <div className="flex-1 text-center">
                            <p className="text-sm font-medium text-neutral-600">{f.airline} {f.flight_number}</p>
                            <div className="h-px bg-neutral-300 my-2 relative">
                              <FaPlane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sky-400 bg-white px-1" />
                            </div>
                          </div>
                          <div className="text-center">
                            <FaPlaneArrival className="text-sky-500 mx-auto mb-1" />
                            <p className="text-lg font-bold">{f.arrival_airport_iata || '???'}</p>
                            {f.arrival_time && <p className="text-sm text-neutral-500">{f.arrival_time}</p>}
                            {f.arrival_date && <p className="text-xs text-neutral-400">{f.arrival_date}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => { setStep('choose'); setScannedData(null); }} className="px-4 py-2 text-neutral-600 hover:text-neutral-800">Try Again</button>
                    <button onClick={handleConfirmScanned} className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 flex items-center gap-2">
                      <FaCheck /> Confirm & Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Ticket Scan Sub-component
const ScanInput = ({ onScan, isProcessing, onBack }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  return (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive ? 'border-sky-400 bg-sky-50' : 'border-neutral-300'}`}
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files[0]) setSelectedFile(e.dataTransfer.files[0]); }}
      >
        <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={(e) => { if (e.target.files[0]) setSelectedFile(e.target.files[0]); }} className="hidden" />
        {selectedFile ? (
          <div className="space-y-4">
            <FaCheckCircle className="text-4xl text-green-500 mx-auto" />
            <p className="font-medium text-neutral-700">{selectedFile.name}</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setSelectedFile(null)} className="px-4 py-2 text-neutral-600 hover:text-neutral-800">Change File</button>
              <button onClick={() => onScan(selectedFile)} disabled={isProcessing} className="px-6 py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 disabled:opacity-50 flex items-center gap-2">
                {isProcessing ? <><FaSpinner className="animate-spin" /> Scanning...</> : <><FaCamera /> Scan Ticket</>}
              </button>
            </div>
          </div>
        ) : (
          <>
            <FaUpload className="text-4xl text-neutral-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-neutral-700 mb-2">Drag & drop your ticket here</p>
            <p className="text-sm text-neutral-500 mb-4">Supports images and PDF files</p>
            <button onClick={() => fileInputRef.current?.click()} className="px-6 py-2 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600">Browse Files</button>
          </>
        )}
      </div>
      <button onClick={onBack} className="text-neutral-500 hover:text-neutral-700 text-sm">Back to options</button>
    </div>
  );
};

// Flight Lookup Sub-component
const LookupInput = ({ onLookup, isProcessing, onBack, startDate, endDate }) => {
  const [flightNumber, setFlightNumber] = useState('');
  const [date, setDate] = useState(startDate || '');

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">Flight Number</label>
        <input type="text" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value.toUpperCase())} placeholder="e.g., UA123" className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">Flight Date</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={startDate} max={endDate} className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500" required />
      </div>
      <div className="flex gap-3 justify-between">
        <button type="button" onClick={onBack} className="text-neutral-500 hover:text-neutral-700">Back to options</button>
        <button onClick={() => flightNumber && date && onLookup(flightNumber, date)} disabled={!flightNumber || !date || isProcessing} className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 flex items-center gap-2">
          {isProcessing ? <><FaSpinner className="animate-spin" /> Looking up...</> : <><FaSearch /> Look Up Flight</>}
        </button>
      </div>
    </div>
  );
};

export default DemoFlightManager;
